/**
 * Deutsche Lokalisierung für react-big-calendar — geteilt von allen Kalendern
 * des öffentlichen Frontends (Raum-, Mitglieder- und Gesamtansicht).
 *
 * Vorher lag dieser Block dreimal kopiert in den Komponenten; die Kopien waren
 * bereits auseinandergelaufen (》Keine Buchungen《 vs. 》Keine Termine《). Die
 * beiden Stellen, an denen das gewollt war, sind hier Parameter — alles andere
 * hat genau eine Quelle.
 */

import { type Formats, dateFnsLocalizer } from "react-big-calendar";
import { format, getDay, parse, startOfWeek } from "date-fns";
import { de } from "date-fns/locale";

import type { EventDetail } from "@/components/EventDetailDialog";
import type { BookingCalendarEvent } from "@/types/api";

const locales = { de } as const;

export const localizer = dateFnsLocalizer({
  format: (date: Date, formatStr: string) => format(date, formatStr, { locale: de }),
  parse: (value: string, formatStr: string) => parse(value, formatStr, new Date(), { locale: de }),
  startOfWeek: (date: Date) => startOfWeek(date, { locale: de }),
  getDay,
  locales,
});

/** ``emptyRange``: was in einem Zeitraum ohne Einträge steht. */
export function calendarMessages(emptyRange = "Keine Buchungen in diesem Zeitraum") {
  return {
    date: "Datum",
    time: "Zeit",
    event: "Termin",
    allDay: "Ganztägig",
    week: "Woche",
    work_week: "Arbeitswoche",
    day: "Tag",
    month: "Monat",
    previous: "Zurück",
    next: "Weiter",
    yesterday: "Gestern",
    tomorrow: "Morgen",
    today: "Heute",
    agenda: "Agenda",
    noEventsInRange: emptyRange,
    showMore: (count: number) => `+${count} weitere`,
  };
}

const SOURCE_LABELS = {
  course_plan: "Website-Stundenplan",
  notion: "Notion-Eventkalender",
  manual: "manuelle Sperre",
} as const;

/**
 * Ende eines Termins für react-big-calendar. Endet er genau um 00:00, rückt
 * das Ende eine Millisekunde vor — sonst zeigt ``showMultiDayTimes`` am
 * Folgetag ein leeres Stück desselben Termins.
 */
export function eventEnd(iso: string): Date {
  const end = new Date(iso);
  return end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0
    ? new Date(end.getTime() - 1)
    : end;
}

/** Daten fürs Detail-Fenster eines Feed-Termins (Quelle nur bei Sperren). */
export function feedEventDetail(
  e: BookingCalendarEvent,
  name: string,
  roomName?: string,
): EventDetail {
  return {
    name,
    roomName,
    start: new Date(e.starts_at),
    end: new Date(e.ends_at),
    sourceLabel: e.kind === "blocked" && e.source ? SOURCE_LABELS[e.source] : null,
    sourceUrl: e.source_url,
  };
}

/** ``rangeSeparator``: Trenner in der Kopfzeile der Wochenansicht. */
export function calendarFormats(rangeSeparator = "–"): Formats {
  return {
    weekdayFormat: (date: Date, _culture, loc) =>
      (loc?.format(date, "EEEEEE") ?? format(date, "EEEEEE", { locale: de })).replace(".", ""),
    dayFormat: (date: Date, _culture, loc) =>
      loc?.format(date, "EEEEEE d.") ?? format(date, "EEEEEE d.", { locale: de }),
    dayHeaderFormat: (date: Date, _culture, loc) =>
      loc?.format(date, "EEEE, d. MMMM") ?? format(date, "EEEE, d. MMMM", { locale: de }),
    dayRangeHeaderFormat: ({ start, end }, _culture, loc) => {
      const s = loc?.format(start, "d. MMM") ?? format(start, "d. MMM", { locale: de });
      const e = loc?.format(end, "d. MMM") ?? format(end, "d. MMM", { locale: de });
      return `${s} ${rangeSeparator} ${e}`;
    },
    timeGutterFormat: (date: Date, _culture, loc) =>
      loc?.format(date, "HH:mm") ?? format(date, "HH:mm", { locale: de }),
    // Die Uhrzeit zeigt das Raster bereits — im Kästchen nur der Name, die
    // genaue Zeit steht im Detail-Fenster.
    eventTimeRangeFormat: () => "",
    // Agenda: Datum steht sonst nur in der ersten Zeile eines Tages (rowspan) —
    // ein abgeschnittener Screenshot verwechselte so Samstag und Sonntag.
    agendaTimeRangeFormat: ({ start, end }, _culture, loc) => {
      const fmt = (d: Date, f: string) => loc?.format(d, f) ?? format(d, f, { locale: de });
      const weekday = fmt(start, "EEEEEE").replace(".", "");
      return `${weekday} ${fmt(start, "dd.MM.")} · ${fmt(start, "HH:mm")}–${fmt(end, "HH:mm")}`;
    },
  };
}
